import { useState } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { 
  Globe, Download, Loader2, Plus, Trash2, Key, Link, 
  CheckCircle, AlertCircle, FileSpreadsheet
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ApiToExcelTab = () => {
  const [apiUrl, setApiUrl] = useState("");
  const [headers, setHeaders] = useState([{ key: "", value: "" }]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const addHeader = () => {
    setHeaders([...headers, { key: "", value: "" }]);
  };

  const removeHeader = (index) => {
    setHeaders(headers.filter((_, i) => i !== index));
  };

  const updateHeader = (index, field, value) => {
    const newHeaders = [...headers];
    newHeaders[index][field] = value;
    setHeaders(newHeaders);
  };

  const handleFetch = async () => {
    if (!apiUrl.trim()) {
      toast.error("Introduce una URL de API");
      return;
    }

    setLoading(true);
    setResult(null);
    setError(null);

    // Build headers object (filter empty ones)
    const headersObj = {};
    headers.forEach(h => {
      if (h.key.trim() && h.value.trim()) {
        headersObj[h.key.trim()] = h.value.trim();
      }
    });

    try {
      const response = await fetch(`${API}/api-to-excel/fetch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          url: apiUrl.trim(),
          headers: headersObj
        })
      });

      const data = await response.json();

      if (response.ok) {
        setResult(data);
        toast.success(`${data.row_count} filas encontradas`);
      } else {
        setError(data.detail || "Error al obtener datos");
        toast.error(data.detail || "Error al obtener datos");
      }
    } catch (err) {
      setError("Error de conexión");
      toast.error("Error de conexión");
    }

    setLoading(false);
  };

  const handleDownload = async () => {
    if (!result?.result_id) return;

    try {
      const response = await fetch(
        `${API}/api-to-excel/download/${result.result_id}`,
        { credentials: "include" }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `api_export_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Excel descargado");
      } else {
        toast.error("Error al descargar");
      }
    } catch (err) {
      toast.error("Error de conexión");
    }
  };

  return (
    <div className="space-y-6" data-testid="api-to-excel-tab">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold">API a Excel</h2>
        <p className="text-muted-foreground">Conecta a cualquier API REST y exporta los datos a Excel</p>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-500/5 border-blue-500/20">
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <Globe className="w-8 h-8 text-blue-500 shrink-0" />
            <div className="text-sm space-y-2">
              <p className="font-semibold text-blue-600 dark:text-blue-400">¿Cómo funciona?</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>Introduce la URL de cualquier API REST que devuelva JSON</li>
                <li>Añade headers de autenticación si es necesario (API Key, Bearer Token, etc.)</li>
                <li>La herramienta detecta automáticamente la estructura de datos</li>
                <li>Descarga los resultados en formato Excel</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Link className="w-5 h-5 text-blue-500" />
            URL de la API
          </CardTitle>
          <CardDescription>
            Introduce la URL completa del endpoint de la API
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Input
              placeholder="https://api.ejemplo.com/data"
              value={apiUrl}
              onChange={(e) => setApiUrl(e.target.value)}
              className="font-mono text-sm"
            />
          </div>

          {/* Headers Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                <Key className="w-4 h-4" />
                Headers (opcional)
              </Label>
              <Button variant="outline" size="sm" onClick={addHeader}>
                <Plus className="w-4 h-4 mr-1" />
                Añadir
              </Button>
            </div>

            {headers.map((header, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  placeholder="Header (ej: Authorization)"
                  value={header.key}
                  onChange={(e) => updateHeader(index, "key", e.target.value)}
                  className="flex-1 font-mono text-sm"
                />
                <Input
                  placeholder="Valor (ej: Bearer token123)"
                  value={header.value}
                  onChange={(e) => updateHeader(index, "value", e.target.value)}
                  className="flex-1 font-mono text-sm"
                />
                {headers.length > 1 && (
                  <Button variant="ghost" size="icon" onClick={() => removeHeader(index)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                )}
              </div>
            ))}

            <p className="text-xs text-muted-foreground">
              Ejemplos: <code className="bg-muted px-1 rounded">Authorization: Bearer tu_token</code>, 
              <code className="bg-muted px-1 rounded ml-1">X-API-Key: tu_api_key</code>
            </p>
          </div>

          <Button 
            onClick={handleFetch} 
            disabled={loading || !apiUrl.trim()}
            className="w-full"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Obteniendo datos...
              </>
            ) : (
              <>
                <Globe className="w-4 h-4 mr-2" />
                Conectar y Obtener Datos
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Card className="border-red-500/30 bg-red-500/5">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-600 dark:text-red-400">Error</p>
                <p className="text-sm text-muted-foreground">{error}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Result */}
      {result && (
        <Card className="border-green-500/30 bg-green-500/5">
          <CardHeader>
            <CardTitle className="text-base text-green-600 dark:text-green-400 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Datos obtenidos correctamente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 rounded-lg bg-background">
                <p className="text-2xl font-bold text-blue-600">{result.row_count?.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Filas</p>
              </div>
              <div className="p-3 rounded-lg bg-background">
                <p className="text-2xl font-bold text-purple-600">{result.column_count}</p>
                <p className="text-xs text-muted-foreground">Columnas</p>
              </div>
              <div className="p-3 rounded-lg bg-background col-span-2">
                <p className="text-sm font-medium truncate">{result.data_type}</p>
                <p className="text-xs text-muted-foreground">Tipo de datos</p>
              </div>
            </div>

            {result.columns && result.columns.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Columnas detectadas:</p>
                <div className="flex flex-wrap gap-1">
                  {result.columns.slice(0, 20).map((col, i) => (
                    <span key={i} className="text-xs px-2 py-1 bg-muted rounded">
                      {col}
                    </span>
                  ))}
                  {result.columns.length > 20 && (
                    <span className="text-xs px-2 py-1 bg-muted rounded text-muted-foreground">
                      +{result.columns.length - 20} más
                    </span>
                  )}
                </div>
              </div>
            )}

            <Button onClick={handleDownload} className="w-full">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Descargar Excel ({result.row_count} filas)
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ApiToExcelTab;
